import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Form, Input, Label } from '@shared/components';
import {
    smriqVehicleSchema,
    type SmriqVehicleFormInput,
    type SmriqVehicleFormOutput,
} from '../../../schemas/smriqJourney.schema';
import type { ConfirmSmriqVehiclePayload } from '../../../services/smriq';
import type { SmriqJourneyError } from '../../../hooks/useSmriqJourney';

interface SmriqVehicleFormProps {
    className?: string;
    defaultVrm: string;
    defaultCurrentMileage: number | undefined;
    defaultAnnualMileage: number | undefined;
    onConfirm: (payload: ConfirmSmriqVehiclePayload) => void;
    onFieldsChange: () => void;
    /** Discards the edit and closes the form. Omitted when there is no confirmed vehicle to
     *  return to (an initial or unverified confirm must be completed, not cancelled). */
    onCancel?: () => void;
    isConfirming: boolean;
    confirmError: SmriqJourneyError | null;
    confirmed: boolean;
    /** Registration is normally fixed by the quote vehicle and shown read-only; true only as a
     *  one-off fallback when no reg reached this page, so it can be entered here. */
    allowRegistrationEdit?: boolean;
}

const VEHICLE_FIELDS = ['vrm', 'current_mileage', 'annual_mileage'] as const;

/** Prefilled from the quote's own vehicle; the mileage fields stay editable, and the
 *  registration too only when the quote vehicle carried none. */
export const SmriqVehicleForm: React.FC<SmriqVehicleFormProps> = ({
    className,
    defaultVrm,
    defaultCurrentMileage,
    defaultAnnualMileage,
    onConfirm,
    onFieldsChange,
    onCancel,
    isConfirming,
    confirmError,
    confirmed,
    allowRegistrationEdit = false,
}) => {
    const defaultValues: SmriqVehicleFormInput = {
        vrm: defaultVrm,
        current_mileage: defaultCurrentMileage !== undefined ? String(defaultCurrentMileage) : '',
        annual_mileage: defaultAnnualMileage !== undefined ? String(defaultAnnualMileage) : '',
    };

    const {
        control,
        handleSubmit,
        setError,
    } = useForm<SmriqVehicleFormInput, unknown, SmriqVehicleFormOutput>({
        mode: 'onBlur',
        resolver: zodResolver(smriqVehicleSchema),
        defaultValues,
    });

    const hasMappedFieldError = VEHICLE_FIELDS.some(
        (name) => !!confirmError?.fieldErrors?.[name]?.[0],
    );
    // The proxy echoes any field this form sends as `param`, not just `vrm`.
    const mappedParam = VEHICLE_FIELDS.find((name) => name === confirmError?.param);

    useEffect(() => {
        if (!confirmError) return;
        VEHICLE_FIELDS.forEach((name) => {
            const message = confirmError.fieldErrors?.[name]?.[0];
            if (message) setError(name, { type: 'server', message });
        });
        if (mappedParam && !confirmError.fieldErrors?.[mappedParam]?.[0]) {
            setError(mappedParam, { type: 'server', message: confirmError.message });
        }
    }, [confirmError, mappedParam, setError]);

    const onSubmit = (data: SmriqVehicleFormOutput) => onConfirm(data);
    const stepLevelError =
        confirmError && !mappedParam && !hasMappedFieldError ? confirmError.message : null;

    return (
        <div className={`bg-mp-surface rounded-xl space-y-4 ${className ?? ''}`}>
            {stepLevelError && (
                <div role="alert" className="text-sm text-mp-error">
                    {stepLevelError}
                </div>
            )}

            <Form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                // An auto-width column per button, so a longer label ("Re-confirm vehicle")
                // never wraps to two lines.
                className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto_auto] gap-4 items-start"
            >
                {/* Registered even when read-only, so the reg is still submitted and a
                    server-side reg error still surfaces. */}
                <div className={allowRegistrationEdit ? undefined : 'flex flex-col self-stretch'}>
                    <Label htmlFor={allowRegistrationEdit ? 'smriq_vrm' : undefined} required={allowRegistrationEdit}>
                        Registration
                    </Label>
                    <Controller
                        name="vrm"
                        control={control}
                        render={({ field, fieldState: { error: fe } }) =>
                            allowRegistrationEdit ? (
                                <Input
                                    id="smriq_vrm"
                                    type="text"
                                    disabled={isConfirming}
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(e);
                                        onFieldsChange();
                                    }}
                                    onBlur={field.onBlur}
                                    error={!!fe}
                                    hint={fe?.message}
                                    hintId="smriq_vrm-hint"
                                    aria-invalid={!!fe}
                                    aria-required
                                    aria-describedby={fe ? 'smriq_vrm-hint' : undefined}
                                />
                            ) : (
                                <div className="flex flex-1 flex-col justify-center">
                                    <p className="text-sm text-mp-foreground">
                                        {field.value}
                                    </p>
                                    {fe && (
                                        <p className="mt-1 text-xs text-mp-error">
                                            {fe.message}
                                        </p>
                                    )}
                                </div>
                            )
                        }
                    />
                </div>

                <div>
                    <Label htmlFor="smriq_current_mileage" required>
                        Current mileage
                    </Label>
                    <Controller
                        name="current_mileage"
                        control={control}
                        render={({ field, fieldState: { error: fe } }) => (
                            <Input
                                id="smriq_current_mileage"
                                type="number"
                                inputMode="numeric"
                                disabled={isConfirming}
                                value={field.value}
                                onChange={(e) => {
                                    field.onChange(e);
                                    onFieldsChange();
                                }}
                                onBlur={field.onBlur}
                                error={!!fe}
                                hint={fe?.message}
                                hintId="smriq_current_mileage-hint"
                                aria-invalid={!!fe}
                                aria-required
                                aria-describedby={fe ? 'smriq_current_mileage-hint' : undefined}
                            />
                        )}
                    />
                </div>

                <div>
                    <Label htmlFor="smriq_annual_mileage" required>
                        Annual mileage
                    </Label>
                    <Controller
                        name="annual_mileage"
                        control={control}
                        render={({ field, fieldState: { error: fe } }) => (
                            <Input
                                id="smriq_annual_mileage"
                                type="number"
                                inputMode="numeric"
                                disabled={isConfirming}
                                value={field.value}
                                onChange={(e) => {
                                    field.onChange(e);
                                    onFieldsChange();
                                }}
                                onBlur={field.onBlur}
                                error={!!fe}
                                hint={fe?.message}
                                hintId="smriq_annual_mileage-hint"
                                aria-invalid={!!fe}
                                aria-required
                                aria-describedby={fe ? 'smriq_annual_mileage-hint' : undefined}
                            />
                        )}
                    />
                </div>

                <div className="flex flex-col self-stretch">
                    <label className="mb-1.5 text-sm font-medium">&nbsp;</label>
                    <Button size="sm" disabled={isConfirming} className="self-baseline whitespace-nowrap">
                        {isConfirming ? 'Confirming...' : confirmed ? 'Re-confirm vehicle' : 'Confirm vehicle'}
                    </Button>
                </div>

                {onCancel && (
                    <div className="flex flex-col self-stretch">
                        <label className="mb-1.5 text-sm font-medium">&nbsp;</label>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={isConfirming}
                            onClick={onCancel}
                            className="whitespace-nowrap"
                        >
                            Cancel
                        </Button>
                    </div>
                )}
            </Form>
        </div>
    );
};
